import { RULE_FIELDS, RULE_OPERATORS, newRule } from './admin-format';
import type { EligibilityRule } from './admin-api';

/**
 * The shared eligibility-rule builder (SCRUM-391) — the v7 `.elig-builder`
 * used by the Products onboarding step: field/operator/
 * value rows with a required toggle, remove, and Add Rule.
 */

export function EligibilityBuilder({
  rules,
  onChange,
  idPrefix,
}: {
  rules: EligibilityRule[];
  onChange: (rules: EligibilityRule[]) => void;
  /** Unique prefix for input ids/labels (multiple builders per page). */
  idPrefix: string;
}) {
  const update = (i: number, patch: Partial<EligibilityRule>) => {
    onChange(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };

  return (
    <div className="flex flex-col gap-2">
      {rules.map((rule, i) => (
        <div key={`${idPrefix}-${String(i)}`} className="flex items-center gap-2">
          <select
            aria-label={`${idPrefix} rule ${String(i + 1)} field`}
            value={rule.field}
            onChange={(e) => {
              update(i, { field: e.target.value });
            }}
            className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md h-8 flex-1 border px-2 text-xs outline-none"
          >
            {RULE_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <select
            aria-label={`${idPrefix} rule ${String(i + 1)} operator`}
            value={rule.operator}
            onChange={(e) => {
              update(i, { operator: e.target.value });
            }}
            className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md h-8 w-[110px] border px-2 text-xs outline-none"
          >
            {RULE_OPERATORS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <input
            aria-label={`${idPrefix} rule ${String(i + 1)} value`}
            value={rule.value}
            onChange={(e) => {
              update(i, { value: e.target.value });
            }}
            className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md h-8 w-[90px] border px-2 text-xs outline-none"
          />
          <label className="text-text-3 flex items-center gap-1 text-[0.6875rem]">
            <input
              type="checkbox"
              checked={rule.required}
              onChange={(e) => {
                update(i, { required: e.target.checked });
              }}
            />
            req
          </label>
          <button
            type="button"
            aria-label={`Remove ${idPrefix} rule ${String(i + 1)}`}
            onClick={() => {
              onChange(rules.filter((_, j) => j !== i));
            }}
            className="text-text-3 hover:text-danger p-1 transition-colors"
          >
            <svg
              viewBox="0 0 16 16"
              width="12"
              height="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="4" y1="4" x2="12" y2="12" />
              <line x1="12" y1="4" x2="4" y2="12" />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => {
          onChange([...rules, newRule()]);
        }}
        className="border-border-1 text-text-2 hover:text-brand-primary hover:border-brand-primary rounded-tk-md self-start border border-dashed px-3 py-1 text-xs transition-colors"
      >
        Add Rule
      </button>
    </div>
  );
}
